import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

const ADDED_STATE_DURATION = 2000;

/** Texte aus layout/theme.liquid (#fk-ui-strings, Sprachdateien); Englisch nur als Rückfall. */
const strings = (() => {
  try {
    return JSON.parse(document.getElementById('fk-ui-strings')?.textContent || '{}');
  } catch {
    return {};
  }
})();
const ADD_ERROR = strings.add_error || 'Could not add this item. Please try again.';
const ADDED = strings.added_to_cart || 'Added to cart';

/**
 * Wraps the plain cart form of a product card (snippets/fk-product-card.liquid) and adds the item
 * without a page load. Follows the contract of Horizon's `product-form-component`: dispatches a
 * `CartLinesUpdateEvent` whose promise resolves with the refreshed cart and the re-rendered cart
 * sections, so cart drawer, cart icon and items update and the drawer auto-opens when enabled.
 * Without JavaScript the form posts to /cart/add as usual.
 */
class FkAddButton extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timeout;

  connectedCallback() {
    this.#controller = new AbortController();
    this.querySelector('form')?.addEventListener('submit', this.#onSubmit, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    clearTimeout(this.#timeout);
  }

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    event.preventDefault();
    const form = /** @type {HTMLFormElement} */ (event.currentTarget);
    const button = form.querySelector('button[type="submit"]');
    if (!(button instanceof HTMLButtonElement) || button.getAttribute('aria-busy') === 'true') return;

    button.setAttribute('aria-busy', 'true');
    const status = this.querySelector('[data-add-status]');
    if (status) status.textContent = '';

    const formData = new FormData(form);
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
      .filter(Boolean);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const quantity = Number(formData.get('quantity')) || 1;
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: /** @type {string} */ (formData.get('id')), quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const config = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      }).then((res) => res.json());

      const cart = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((res) => res.json());

      const detail = {
        items: cart.items,
        source: 'fk-add-button',
        sourceId: this.dataset.productId,
        itemCount: quantity,
        productId: this.dataset.productId,
      };

      if (response.status) {
        if (status) status.textContent = response.description || response.message || ADD_ERROR;
        this.dispatchEvent(new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID' }));
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { ...detail, didError: true } });
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { ...detail, sections: response.sections, didError: false },
      });
      this.#showAdded();
      this.dispatchEvent(new CustomEvent('fk:cart-added', { bubbles: true }));
    } catch (error) {
      if (status) status.textContent = ADD_ERROR;
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({ error: error instanceof Error ? error.message : 'Network error', code: 'SERVICE_UNAVAILABLE' })
      );
    } finally {
      button.removeAttribute('aria-busy');
    }
  };

  #showAdded() {
    clearTimeout(this.#timeout);
    this.dataset.state = 'added';
    const status = this.querySelector('.fk-pcard__status');
    if (status) status.textContent = ADDED;
    this.#timeout = setTimeout(() => {
      delete this.dataset.state;
      if (status) status.textContent = '';
    }, ADDED_STATE_DURATION);
  }
}

if (!customElements.get('fk-add-button')) {
  customElements.define('fk-add-button', FkAddButton);
}
